<template lang="pug">
    ._crsl(
        :class="vertical ? '_crsl-v' : '_crsl-h'",
        :style="{ width, height }",
        v-size="resize"
    )
        ._crsh(
            @wheel.prevent="wheel"
        )
            ._crso(
                ref="outer",
                :class="{ _cios: $device.ios, _csnap: !mouse }",
                @scroll="scroll"
            )
                ._crsi(
                    ref="inner",
                    @mousedown="mousedown",
                    @touchstart="touchstart"
                )
                    slot

        ol._crst(
            :class="{ _hide: child.length < 2 }",
            role="tablist")
            li(v-for="(c,i) in child",
                role="tab",
                :aria-selected="ivalue == i",
                @click="goto(i)")
</template>

<script>
    import $util from '../../global/util';

    const haveSnapScroll =
        'scrollSnapType' in document.documentElement.style ||
        'webkitScrollSnapType' in document.documentElement.style ||
        'msScrollSnapType' in document.documentElement.style;

    const wheelThreshold = 1;
    const wheelThrottle = 150;    // ms
    const scrollTimeout = 300;    // ms


    /**
     * This is a user interface element that allows different content blocks to share the same screen space in a
     * mutually exclusive manner. The user can pan content by dragging the content area, or by clicking an
     * indicator dot. This is implemented using Scroll Snap, to allow fluid movement on mobile devices.
     * @see Each seat is represented as an instance of [ib-col](#/components/layout/ib-col).
     */
    export default {
        name: 'ib-carousel',

        props: {
            /**
             * The index of the seat to display. If not specified, the first seat will be shown.
             */
            value: Number,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String,
            /**
             * Vertical carousel, works best with set height.
             */
            vertical: Boolean
        },

        data() {
            return {
                child: [],
                ivalue: -1,
                grab: false,
                mouse: false,
                fm: false,
                sl: 0,
                cx: 0,
                ts: 0,
                tw: 0,
                left: 0,
                observer: null
            };
        },

        computed: {
            sp() {
                return $util.scrollProps(this.$rtl);
            }
        },

        watch: {
            value(val) {
                this.goto(val);
            },
            dir: 'resize'
        },

        mounted() {
            this._mutate();
            if (window.MutationObserver) {
                this.observer = new MutationObserver(this._mutate);
                this.observer.observe(this.$refs.inner, { childList: true });
            }
            this.$nextTick(() => {
                this.goto(this.value, 'auto');
                this.scroll();
            });
        },

        beforeDestroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        },

        methods: {
            /**
             * Navigate to the seat before the active one, if any.
             */
            prev() {
                if (this.ivalue > 0) {
                    this.goto(this.ivalue - 1);
                }
            },

            /**
             * Navigate to the seat after the active one, if any.
             */
            next() {
                if (this.ivalue < this.child.length - 1) {
                    this.goto(this.ivalue + 1);
                }
            },

            /**
             * Navigate to a seat by index.
             */
            goto(index, behavior = 'smooth') {
                const outer = this.$refs.outer;
                if (outer) {
                    this.scrollSelf = Date.now() + scrollTimeout;
                    index = Math.round(Math.max(0, Math.min(this.child.length - 1, index))) | 0;
                    if (this.ivalue !== index) {
                        this.ivalue = index;
                        this.$emit('input', index);
                        if (this.vertical) {
                            const top = Math.round(index) * outer.clientHeight;
                            if (top != outer.scrollTop) {
                                this.top = top;
                                outer.scrollTo({ top, behavior });
                                return true;
                            } else {
                                this.scroll();
                            }
                        } else {
                            this.end();
                            const left = this.sp.base * outer.scrollWidth + (this.sp.step * Math.round(index) - this.sp.base) * outer.clientWidth;
                            if (left != outer.scrollLeft) {
                                // this.ivalue will be updated by scroll event
                                this.left = left;
                                outer.scrollTo({ left, behavior });
                                return true;
                            } else {
                                this.scroll();
                            }
                        }
                    }
                }
            },

            /**
             * Capture or release mouse.
             * @private
             */
            capture(fn, event) {
                let outer = this.$refs.outer;
                if (outer[fn]) {
                    outer[fn]();
                } else {
                    document.documentElement[event]('mouseout', this.mouseout);
                    outer = document.body;
                }
                outer[event]('mousemove', this.mousemove);
                outer[event]('mouseup', this.mouseup);
            },

            /**
             * Mouse down event.
             * @private
             */
            mousedown(e) {
                const outer = this.$refs.outer;
                if (!e.button) {
                    this.capture('setCapture', 'addEventListener');
                    this.start();
                    this.mouse = true;
                    this.sl = outer.scrollLeft;
                    this.cx = e.clientX;
                    this.scrollSelf = 0;
                    outer.classList.add('_crsg');
                }
            },

            mouseup(e) {
                this.snap(this.cx - e.clientX);
                this.mouse = false;
                const outer = this.$refs.outer;
                outer.classList.remove('_crsg');
                e.preventDefault();
            },

            mousemove(e) {
                this.move(e.clientX);
                e.preventDefault();
            },

            mouseout(e) {
                if (e.target === document.documentElement) {
                    this.mouseup(e);
                }
            },

            /**
             * Touch start event.
             * @private
             */
            touchstart(e) {
                const outer = this.$refs.outer;
                if (e.touches.length == 1) {
                    document.addEventListener('touchend', this.touchend, false);
                    this.start();
                    this.sl = outer.scrollLeft;
                    this.cx = e.touches[0].clientX;
                    this.scrollSelf = 0;
                }
            },

            touchmove(e) {
                this.move(e.touches[0].clientX);
                e.preventDefault();
            },

            touchend(e) {
                const outer = this.$refs.outer;
                outer.removeEventListener('touchmove', this.touchmove);
                document.removeEventListener('touchend', this.touchend, false);
                if (haveSnapScroll && !this.mouse) {
                    this.end();
                } else {
                    this.snap(outer.scrollLeft - this.sl);
                }
            },

            /**
             * Mouse wheel event.
             * @private
             */
            wheel(e) {
                const outer = this.$refs.outer;
                const now = Date.now();
                if (this.tw < now - wheelThrottle) {
                    const x = e.deltaX;
                    const y = e.deltaY;
                    const go =
                        x < -wheelThreshold || x > wheelThreshold ? x :
                        y < -wheelThreshold || y > wheelThreshold ? y : 0;
                    if (go) {
                        this.tw = now;
                        if (this.goto(this.ivalue + (go > 0 ? 1 : -1))) {
                            outer.classList.add('_crsw');
                        }
                    }
                }
            },

            /**
             * Set scroll position during drag.
             * @private
             */
            move(x) {
                const outer = this.$refs.outer;
                // Prevent click event on children after first move
                if (this.fm) {
                    this.fm = false;
                    outer.classList.add('_crsp');
                }
                outer.scrollTo({
                    left: this.sl + this.cx - x
                });
            },

            /**
             * Begin grabbing after mouse or touch start.
             * @private
             */
            start() {
                this.grab = true;
                this.fm = true;
                this.ts = Date.now();
            },

            /**
             * End grabbing after mouse or touch end.
             * @private
             */
            end() {
                if (this.grab) {
                    this.grab = false;
                    this.capture('releaseCapture', 'removeEventListener');
                    const outer = this.$refs.outer;
                    outer.classList.remove('_crsp');
                }
            },

            /**
             * Scroll into view with momentum after mouse or touch end.
             * @private
             */
            snap(delta) {
                const outer = this.$refs.outer;
                const duration = Date.now() - this.ts;
                const cw = outer.clientWidth;
                if (duration > 20) {
                    delta += delta * Math.abs(delta / duration);
                    delta = Math.min(cw, Math.max(-cw, delta));
                }
                this.goto((this.sp.base * outer.scrollWidth + this.sp.step * (this.sl + delta)) / cw - this.sp.base);
            },

            /**
             * Scroll event.
             * @private
             */
            scroll() {
                let scrollSelf = this.scrollSelf;
                if (scrollSelf) {
                    const now = Date.now();
                    this.scrollSelf = scrollSelf = now > scrollSelf ? 0 : now + scrollTimeout;
                }
                if (!scrollSelf) {
                    const outer = this.$refs.outer;
                    if (outer) {
                        let index;
                        let done;
                        if (this.vertical && outer.clientHeight) {
                            index = Math.round(outer.scrollTop / outer.clientHeight);
                            done = outer.scrollTop === this.top;
                        } else if (outer.clientWidth) {
                            index = Math.round((this.sp.base * outer.scrollWidth + this.sp.step * outer.scrollLeft) / outer.clientWidth - this.sp.base);
                            done = outer.scrollLeft === this.left;
                        }
                        if (this.ivalue !== index) {
                            this.ivalue = index;
                            this.$emit('input', index);
                        }
                        if (done) {
                            outer.classList.remove('_crsw');
                        }
                    }
                }
            },

            /**
             * Resize event.
             * @private
             */
            resize() {
                this.goto(this.ivalue, 'auto');
            },

            /**
             * Recompute the child list.
             * @private
             */
            _mutate() {
                const inner = this.$refs.inner;
                const child = [].slice.call(inner.children);
                this.child = child;
                if (this.ivalue >= 0) {
                    this.goto(this.ivalue, 'auto');
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._crsl {
        display: flex;
        position: relative;
        width: 100%;
        max-width: 100%;
        max-height: 100%;
    }
    ._crsl-h {
        flex-direction: column;
    }
    ._crsl-v {
        flex-direction: row;
    }
    ._crsh {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        overflow: hidden;
    }
    ._crso {
        cursor: -webkit-grab;
        cursor: grab;
        .hide-scrollbar();
    }
    ._crsl-h ._crso {
        overflow-x: scroll;
        overflow-y: auto;
    }
    ._crsl-v ._crso {
        overflow-x: auto;
        overflow-y: scroll;
    }
    ._crsi {
        display: flex;
        flex: 1 0 auto;
        img {
            -webkit-user-drag: none;
            user-drag: none;
        }
    }
    ._crsl-h ._crsi {
        flex-direction: row;
        > * {
            scroll-snap-align: start;
        }
    }
    ._crsl-v ._crsi {
        flex-direction: column;
        > * {
            scroll-snap-align: start;
        }
    }
    ._cios {
        > * {
            /** iOS cannot set snap align on axes independently */
            scroll-snap-align: start;
        }
    }
    ._crst {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 8px 0;
        list-style: none;
        line-height: 0;
        > li {
            margin: 0;
            padding: 4px;
            opacity: .2;
            cursor: pointer;
            border-radius: 50%;
            transition: opacity .2s linear;
            &[aria-selected=true] {
                opacity: .5;
            }
            &:hover {
                opacity: .4;
            }
            &::before {
                content: '';
                display: inline-block;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: @fg;
            }
        }
        &._hide {
            visibility: hidden;
        }
    }
    ._crsl-h ._crst {
        flex-direction: row;
    }
    ._crsl-v ._crst {
        flex-direction: column;
    }
    ._crsp,
    ._crsw {
        pointer-events: none;
    }
    ._csnap {
        -webkit-scroll-snap-type: mandatory;
        -webkit-scroll-snap-destination: 0% 0%;
        -webkit-overflow-scrolling: touch;
        -ms-scroll-snap-type: mandatory;
        scroll-snap-type: mandatory;
    }
    ._crsl-h ._csnap {
        -webkit-scroll-snap-points-x: repeat(100%);
        scroll-snap-type: x mandatory;
        scroll-snap-points-x: repeat(100%);
    }
    ._crsl-v ._csnap {
        -webkit-scroll-snap-points-y: repeat(100%);
        scroll-snap-type: y mandatory;
        scroll-snap-points-y: repeat(100%);
    }
    ._crsg {
        cursor: -webkit-grabbing;
        cursor: grabbing;
        -webkit-scroll-snap-type: none;
        -ms-scroll-snap-type: none;
        scroll-snap-type: none;
    }
</style>

<ib-example id="icons" name="Icons">
    <template lang="pug">
        include ../../icon/flag
        div

            ib-toggle(id="vertical", v-model="vertical") Vertical

            ib-carousel(:vertical="vertical", :height="vertical ? '128px' : null")
                .ib-col(class="text-center icon-128")
                    +icon-flag-us
                .ib-col(class="text-center icon-128")
                    +icon-flag-gb
                .ib-col(class="text-center icon-128")
                    +icon-flag-ru
                .ib-col(class="text-center icon-128")
                    +icon-flag-eu
    </template>
    <script>
        {
            data() {
                return {
                    vertical: false,
                };
            }
        }
    </script>
</ib-example>
